import { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Card,
  Typography,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import logo from "assets/images/logo.png";
import { forgotPassword } from "../../../axios-client";
import { Link } from "react-router-dom";
import LanguageSwitcher from "../../../reusableComponents/LanguageSwitcher/LanguageSwitcher";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import {setLayout} from "../../../store/actions/app-actions";
import {useSoftUIController} from "../../../context";
import SoftInput from "components/SoftInput";
import background from "../../../assets/images/curved-images/background.png";


function ResetPassword() {

  const theme = useTheme();
  const {i18n} = useTranslation();
  const { t } = useTranslation(['resetPassword']);
  const currentLang = i18n.language;
  const features = t('panel.features', { returnObjects: true });

  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
    const [controller, dispatch] = useSoftUIController();

  const [animateTitle, setAnimateTitle] = useState(false);
  const [animateForm, setAnimateForm] = useState(false);
  const [animateImage, setAnimateImage] = useState(false);

  useEffect(() => {
      setTimeout(() => setAnimateImage(true), 300);
      setTimeout(() => setAnimateTitle(true), 500);
      setTimeout(() => setAnimateForm(true), 700);
      setLayout(dispatch, "page");

  }, []);

  useEffect(() => {
    setErrorMessage(null)
  }, [i18n.language]);

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLoading(true);

    if (!email) {
      setErrorMessage(t('enterEmail'));
      setIsLoading(false);
      return;
    }

    try {
      await forgotPassword(email, currentLang);
      setSuccessMessage(t('success'));
    } catch (error) {
      if (error.response?.data?.message) {
        if(error.response.data.message === "No account found for this email."){
          setErrorMessage(t('noAccountFound'));
        } else {
          setErrorMessage(error.response.data.message);
        }
      } else {
        setErrorMessage(t('error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <>

        <Grid container sx={{minHeight: "100vh", overflow: "hidden"}}>
          {/* Left Side */}
          <Grid
              item
              xs={12}
              md={6}
              sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  p: 5,
                  backgroundColor: "#ffffff",
                  position: "relative",
              }}
          >
            <Box
                sx={{
                  position: "absolute",
                  top: theme.spacing(2),
                  left: theme.spacing(2),
                  right: theme.spacing(2),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  zIndex: 10,
                }}
            >
              <Box
                  sx={{
                    transition: "all 0.8s ease",
                  }}
              >
                <img src={logo} alt="YaiGlobal Logo" style={{height: 48}}/>
              </Box>

              <LanguageSwitcher/>
            </Box>

            <Box sx={{width: "100%", maxWidth: 400}}>
              <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: "linear-gradient(to right, #d946ef, #ec4899)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    mb: 1,
                    opacity: animateTitle ? 1 : 0,
                    transform: animateTitle ? "translateX(0)" : "translateX(-10px)",
                    transition: "all 0.7s ease",
                  }}
              >
                {t('title')}
              </Typography>
              <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    mb: 3,
                    opacity: animateTitle ? 1 : 0,
                    transform: animateTitle ? "translateX(0)" : "translateX(-10px)",
                    transition: "all 0.8s ease",
                  }}
              >
                {t('description')}
              </Typography>

              {errorMessage && (
                  <Alert severity="error" sx={{mb: 2}} onClose={() => setErrorMessage(null)}>
                    {errorMessage}
                  </Alert>
              )}
              {successMessage && (
                  <Alert severity="success" sx={{mb: 2}} onClose={() => setSuccessMessage(null)}>
                    {successMessage}
                  </Alert>
              )}

              <Card
                  elevation={4}
                  sx={{
                    borderRadius: 2,
                    p: 3,
                    opacity: animateForm ? 1 : 0,
                    transform: animateForm ? "translateY(0)" : "translateY(10px)",
                    transition: "all 0.7s ease",
                  }}
              >
                <Box component="form" onSubmit={handleForgotPassword}>
                  <Box mb={2}>
                    <Typography variant="caption" fontWeight="bold" sx={{mb: 0.5}}>
                      {t('emailLabel')}
                    </Typography>
                        <SoftInput
                          type="email"
                          placeholder={t('emailPlaceholder')}
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                        />
                  </Box>

                  {isLoading && (
                      <Box display="flex" justifyContent="center" my={2}>
                        <CircularProgress size={24}/>
                      </Box>
                  )}

                  <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        height: 40,
                        background: "linear-gradient(90deg, #d946ef, #ec4899)",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 500,
                        borderRadius: 1,
                        "&:hover": {
                          background: "linear-gradient(90deg, #b91cdb, #d946ef)",
                        },
                      }}
                  >
                    {isLoading ? t('sendingResetLink') : t('sendResetLink')}
                  </Button>

                  <Typography
                      variant="caption"
                      align="center"
                      sx={{mt: 3}}
                  >
                    {t('forgotPassword')}{" "}
                    <Link
                        to="/authentication/sign-in"
                        style={{
                          color: "#d946ef",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                    >
                      {t('signIn')}
                    </Link>
                  </Typography>
                </Box>
              </Card>
            </Box>
          </Grid>

          {/* Right Side – Decorative Panel */}
          <Grid item md={6} sx={{display: {xs: "none", md: "block"}, position: "relative", overflow: "hidden"}}>
              <Box
                  sx={{
                      width: "100%",
                      height: "100%",
                      backgroundImage: `url(${background})`,
                      backgroundRepeat: "no-repeat",
                      filter: "brightness(1.2) contrast(1.2) saturate(1.3)",
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: animateImage ? 0.8 : 0,
                      transition: "opacity 1s ease",
                  }}>
            </Box>
          </Grid>
        </Grid>
      </>
  );
}

export default ResetPassword;